import { z } from 'zod';

/**
 * Entity references in generated forms (architecture documentation section 8.3).
 *
 * A component's configuration schema is the single definition the API validates
 * against and the browser builds a form from. That works well for enums and
 * numbers, but a `z.string().uuid()` carries no hint about *what* it identifies,
 * so the generated form could only offer a text box -- and asking a user to
 * paste a raw identifier is not a form, it is a database prompt in disguise.
 *
 * Marking the field with `entityReference('infrastructure_asset')` attaches the missing
 * piece: the kind of thing the identifier names. Zod carries that metadata into
 * the JSON Schema the catalog endpoint ships (`x-entity-ref`), and the form
 * renders a searchable picker of real names instead. The declaration stays next
 * to the validation rule, so a new configuration field cannot acquire a text
 * box for identifiers by omission.
 */

/**
 * Kinds a reference may point at. Each maps to a list endpoint the picker can
 * search; adding a kind here means teaching the frontend picker to resolve it,
 * which is why the set is closed rather than an open string.
 *
 * `project` is deliberately absent. A deployment is about one project and the
 * shell already knows which (architecture documentation section 2), so a
 * configuration field naming one would be a question with a single possible
 * answer -- and a way to point a dashboard tile at something other than the
 * project the rest of the screen is about. It belongs back here the day a
 * portfolio view does.
 */
export const ENTITY_REFERENCE_KINDS = ['infrastructure_asset'] as const;
export type EntityReferenceKind = (typeof ENTITY_REFERENCE_KINDS)[number];

/**
 * JSON Schema extension keyword. The `x-` prefix keeps it a legal annotation:
 * validators ignore keywords they do not know, so the schema stays usable by
 * anything that consumes it generically.
 */
export const ENTITY_REFERENCE_KEY = 'x-entity-ref';

/** A UUID field that names something the user can pick by name. */
export function entityReference(kind: EntityReferenceKind) {
  return z
    .string()
    .uuid()
    .meta({ [ENTITY_REFERENCE_KEY]: kind });
}

/**
 * Reads the annotation back off a JSON Schema node, returning null for anything
 * that is not a recognised reference.
 *
 * Unknown kinds are treated as absent rather than passed through: a frontend
 * older than the API would otherwise be asked to render a picker for a kind it
 * cannot resolve, and falling back to the plain control degrades better than
 * rendering an empty dropdown.
 */
export function entityReferenceKindOf(node: unknown): EntityReferenceKind | null {
  if (typeof node !== 'object' || node === null) return null;
  const value = (node as Record<string, unknown>)[ENTITY_REFERENCE_KEY];
  return ENTITY_REFERENCE_KINDS.includes(value as EntityReferenceKind)
    ? (value as EntityReferenceKind)
    : null;
}
